import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Panel, Stat } from "@/components/Panel";
import { toast } from "sonner";
import { RefreshCw, Mail, Building2, User, Briefcase, ChevronRight, Inbox, BookOpen, Cpu, Eye } from "lucide-react";

// Phase 36.12 — Unified leads triage console for super-admin.
// Three lead pipelines in one Bloomberg-styled table:
//   1. Industry leads (Mining / Shipping / Heavy Machinery — from /industries/*)
//   2. Founder's Guide downloads
//   3. Industrial Adapter waitlist

const TABS = [
  { key: "industry_leads", label: "INDUSTRY LEADS", Icon: Inbox },
  { key: "founder_guide_leads", label: "FOUNDER'S GUIDE", Icon: BookOpen },
  { key: "industrial_waitlist", label: "INDUSTRIAL WAITLIST", Icon: Cpu },
];

const STATUS_COLORS = {
  new: "border-[#FFB000]/40 text-[#FFB000] bg-[#FFB000]/5",
  contacted: "border-[#00BFFF]/40 text-[#00BFFF] bg-[#00BFFF]/5",
  qualified: "border-[#00E676]/40 text-[#00E676] bg-[#00E676]/5",
  won: "border-[#00E676]/60 text-[#00E676] bg-[#00E676]/10",
  lost: "border-zinc-700 text-zinc-500 bg-zinc-900/20",
};
const STATUS_OPTIONS = ["new", "contacted", "qualified", "won", "lost"];

export default function AdminLeads() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("industry_leads");
  const [industryFilter, setIndustryFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/leads-unified");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/industry-leads/${id}/status`, { status, note: "" });
      toast.success(`Status → ${status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const visibleRows = useMemo(() => {
    if (!data) return [];
    if (tab === "industry_leads") {
      const rows = data.industry_leads || [];
      if (industryFilter === "all") return rows;
      return rows.filter(r => r.industry === industryFilter);
    }
    return data[tab] || [];
  }, [data, tab, industryFilter]);

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="admin-leads-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">// SALES TRIAGE · UNIFIED LEAD CONSOLE</div>
          <h1 className="font-heading font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight mt-1">Leads</h1>
          <p className="text-xs text-zinc-500 max-w-xl mt-1.5">
            Every prospect that hit the marketing surface area — industry pages, Founder's Guide, Industrial waitlist. Triage, mark contacted, close out.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="leads-refresh"
          className="border border-zinc-800 hover:border-zinc-700 px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-zinc-300 flex items-center gap-2"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> REFRESH
        </button>
      </div>

      {/* Aggregate strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900">
        <Stat
          testId="leads-total"
          label="GRAND TOTAL"
          value={data?.totals?.grand_total ?? "—"}
          accent="white"
        />
        <Stat
          testId="leads-industry"
          label="INDUSTRY LEADS"
          value={data?.totals?.industry_leads ?? "—"}
          accent="amber"
        />
        <Stat
          testId="leads-fg"
          label="FOUNDER'S GUIDE"
          value={data?.totals?.founder_guide_leads ?? "—"}
          accent="green"
        />
        <Stat
          testId="leads-industrial"
          label="INDUSTRIAL WAITLIST"
          value={data?.totals?.industrial_waitlist ?? "—"}
          accent="white"
        />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-px bg-zinc-900 border border-zinc-900" data-testid="leads-tabs">
        {TABS.map(t => {
          const TIcon = t.Icon;
          const isActive = t.key === tab;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`leads-tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-2.5 font-mono text-[11px] uppercase tracking-widest transition-colors ${
                isActive
                  ? "bg-black text-[#FFB000]"
                  : "bg-zinc-950 text-zinc-500 hover:text-white"
              }`}
            >
              <TIcon className="w-3.5 h-3.5" />
              {t.label}
              <span className="ml-1 text-zinc-600">
                {data?.totals?.[t.key] ?? "—"}
              </span>
            </button>
          );
        })}
      </div>

      {/* Industry filter strip — only on industry-leads tab */}
      {tab === "industry_leads" && (
        <div className="flex items-center gap-2" data-testid="leads-industry-filter">
          {["all", "shipping", "mining", "heavy-machinery"].map(f => (
            <button
              key={f}
              onClick={() => setIndustryFilter(f)}
              className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest border ${
                industryFilter === f
                  ? "border-[#FFB000] text-[#FFB000] bg-[#FFB000]/10"
                  : "border-zinc-800 text-zinc-500 hover:text-white"
              }`}
            >
              {f === "all" ? "ALL" : f.replace(/-/g, " ")}
            </button>
          ))}
        </div>
      )}

      {/* Records */}
      <Panel title={`// ${TABS.find(t => t.key === tab)?.label || ""}`} testId="leads-records">
        {loading && (
          <div className="p-8 text-center font-mono text-xs text-zinc-600">LOADING…</div>
        )}
        {!loading && visibleRows.length === 0 && (
          <div className="p-10 text-center" data-testid="leads-empty">
            <Inbox className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
            <div className="font-mono text-xs text-zinc-500">No leads in this pipeline yet.</div>
          </div>
        )}
        <div className="divide-y divide-zinc-900">
          {visibleRows.map(r => (
            <LeadRow key={r.id} row={r} kind={tab} onSetStatus={setStatus} />
          ))}
        </div>
      </Panel>
    </div>
  );
}

function LeadRow({ row, kind, onSetStatus }) {
  const status = row.lead_status || "new";
  const [journey, setJourney] = useState(null);
  const [showJourney, setShowJourney] = useState(false);
  const loadJourney = async () => {
    if (!row.visitor_session || journey) return;
    try {
      const r = await api.get(`/admin/visitor-session/${row.visitor_session}/journey`);
      setJourney(r.data);
    } catch (e) {
      setJourney({ items: [], count: 0 });
    }
  };

  return (
    <div className="p-4 hover:bg-zinc-950/30" data-testid={`lead-row-${row.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-heading font-bold text-base">
              {row.name || row.email}
            </span>
            {kind === "industry_leads" && row.industry && (
              <span className="px-2 py-0.5 border border-[#FFB000]/40 text-[#FFB000] bg-[#FFB000]/5 font-mono text-[9px] uppercase tracking-widest">
                {row.industry.replace(/-/g, " ")}
              </span>
            )}
            {kind === "industrial_waitlist" && row.queue_position != null && (
              <span className="px-2 py-0.5 border border-zinc-700 font-mono text-[9px] text-zinc-400">
                #{row.queue_position}
              </span>
            )}
            {row.visitor_session && (
              <button
                type="button"
                onClick={() => { loadJourney(); setShowJourney(s => !s); }}
                data-testid={`lead-journey-toggle-${row.id}`}
                className="px-2 py-0.5 border border-zinc-800 hover:border-[#00E676] hover:text-[#00E676] text-zinc-500 font-mono text-[9px] uppercase tracking-widest flex items-center gap-1"
                title="View this prospect's pre-conversion page journey"
              >
                <Eye className="w-3 h-3" />
                JOURNEY{journey?.count ? ` · ${journey.count}` : ""}
              </button>
            )}
          </div>
          <div className="font-mono text-[11px] text-zinc-500 mt-1 flex items-center gap-3 flex-wrap">
            <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {row.email}</span>
            {row.company && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {row.company}</span>}
            {row.role && <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> {row.role}</span>}
          </div>
          {row.message && (
            <p className="text-xs text-zinc-300 mt-2 leading-relaxed">{row.message}</p>
          )}
          {kind === "industrial_waitlist" && (row.protocols?.length || row.plants_count) && (
            <div className="font-mono text-[10px] text-zinc-500 mt-1.5">
              {row.plants_count ? `${row.plants_count} plants · ` : ""}{(row.protocols || []).join(" · ") || ""}
            </div>
          )}
          <div className="font-mono text-[10px] text-zinc-700 mt-1.5">
            {row.created_at ? new Date(row.created_at).toLocaleString() : ""}
            {row.submission_count > 1 && ` · ${row.submission_count} submissions`}
            {row.download_count > 0 && ` · ${row.download_count} downloads`}
          </div>
          {showJourney && journey && (
            <div className="mt-3 border border-zinc-900 bg-black p-3" data-testid={`lead-journey-${row.id}`}>
              <div className="font-mono text-[9px] uppercase tracking-widest text-[#00E676] mb-2">
                // VISITOR JOURNEY · {journey.count} PAGES
              </div>
              {journey.items.length === 0 ? (
                <div className="font-mono text-[10px] text-zinc-600">
                  Session expired before this lead converted (this lead came from a different browser session).
                </div>
              ) : (
                <ol className="space-y-1">
                  {journey.items.map((step, i) => (
                    <li key={i} className="font-mono text-[10px] text-zinc-400 flex items-start gap-2">
                      <span className="text-zinc-700">{String(i + 1).padStart(2, "0")}.</span>
                      <span className="text-white">{step.path}</span>
                      {step.referrer && <span className="text-zinc-600">· from {(step.referrer || "").slice(0, 40)}</span>}
                      <span className="text-zinc-700 ml-auto flex-shrink-0">{new Date(step.created_at).toLocaleString()}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}
        </div>
        {kind === "industry_leads" && (
          <div className="flex flex-col items-end gap-2">
            <span className={`px-2 py-0.5 border font-mono text-[9px] uppercase tracking-widest ${STATUS_COLORS[status] || STATUS_COLORS.new}`}>
              {status}
            </span>
            <select
              value={status}
              onChange={(e) => onSetStatus(row.id, e.target.value)}
              data-testid={`lead-status-${row.id}`}
              className="bg-black border border-zinc-800 px-2 py-1 font-mono text-[10px] text-white focus:outline-none focus:border-[#FFB000]"
            >
              {STATUS_OPTIONS.map(s => (
                <option key={s} value={s}>{s.toUpperCase()}</option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}
